import React, { useEffect, useState, useCallback, useRef } from "react";
import { connect } from "react-redux";
import HttpService from "../../services/httpService";
import { setSnackInfo } from "../../redux/actions/appAction";
import {
    Grid,
    Paper,
    Card,
    CardContent,
    TextField,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    TableContainer,
    TablePagination,
    Collapse,
    IconButton,
    Chip,
    CircularProgress,
    LinearProgress,
    InputAdornment,
    Tooltip as MuiTooltip,
    withStyles,
} from "@material-ui/core";
import { Skeleton } from "@material-ui/lab";
import Autocomplete from "@material-ui/lab/Autocomplete";
import { makeStyles } from "@material-ui/core/styles";
import TuneIcon from "@material-ui/icons/Tune";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import PeopleAltIcon from "@material-ui/icons/PeopleAlt";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import TrendingUpIcon from "@material-ui/icons/TrendingUp";
import GetAppIcon from "@material-ui/icons/GetApp";
import InboxOutlinedIcon from "@material-ui/icons/InboxOutlined";
import RefreshIcon from "@material-ui/icons/Refresh";
import RecordVoiceOverIcon from "@material-ui/icons/RecordVoiceOver";
import MemoryOutlinedIcon from "@material-ui/icons/MemoryOutlined";
import EmojiObjectsOutlinedIcon from "@material-ui/icons/EmojiObjectsOutlined";
import HistoryOutlinedIcon from "@material-ui/icons/HistoryOutlined";
import CompareArrowsOutlinedIcon from "@material-ui/icons/CompareArrowsOutlined";
import LayersOutlinedIcon from "@material-ui/icons/LayersOutlined";
import FilterNoneOutlinedIcon from "@material-ui/icons/FilterNoneOutlined";
import ReportProblemOutlinedIcon from "@material-ui/icons/ReportProblemOutlined";
import AutorenewOutlinedIcon from "@material-ui/icons/AutorenewOutlined";
import DateRangeOutlinedIcon from "@material-ui/icons/DateRangeOutlined";
import PlaceOutlinedIcon from "@material-ui/icons/PlaceOutlined";
import { Button } from "antd";
import moment from "moment";
import XLSX from "xlsx";
import {
    Chart as ChartJS,
    ArcElement,
    CategoryScale,
    LinearScale,
    BarElement,
    Tooltip,
    Legend,
} from "chart.js";
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(ArcElement, CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const GOOD = "#0ca30c";
const CRITICAL = "#d03b3b";
const HOLD = "#d97706";
const BLUE = "#2a78d6";
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const INK = "#28333f";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const GREEN_GRADIENT = "linear-gradient(135deg, #146c43, #1e8449)";
// Dedicated palette for the Verdict-split donut only (kept separate from the
// green/red used on the Real/Junk chips and stat tiles elsewhere on this page).
const DONUT_REAL_COLOR = "#0d9488";
const DONUT_HOLD_COLOR = "#f59e0b";
const DONUT_JUNK_COLOR = "#e11d48";
const DONUT_COLORS = { real: DONUT_REAL_COLOR, hold: DONUT_HOLD_COLOR, junk: DONUT_JUNK_COLOR };
const VERDICT_ORDER = ["real", "hold", "junk"];

// Rows are separated by a single hairline instead of a full spreadsheet grid.
const cellBorder = { borderBottom: "1px solid #edf0f5", padding: "11px 14px" };

// Brand-coloured indeterminate bar, shown inside the cards whose content is
// being refreshed - the chart panels and the table.
const CardLinearProgress = withStyles(() => ({
    root: {
        height: 4,
        borderRadius: 999,
        backgroundColor: "rgba(193,83,31,0.10)",
    },
    bar: {
        borderRadius: 999,
        background: `linear-gradient(90deg, ${ORANGE_DARK}, ${ORANGE}, #ff9c66)`,
    },
}))(LinearProgress);

const StyledHeadCell = withStyles(() => ({
    root: {
        // Solid, not a gradient: across a wide table the gradient's lighter end
        // left the right-hand headers washed out, and a flat band keeps every
        // label at the same contrast. Matches the other redesigned tables.
        background: ORANGE_DARK,
        // Pure white, not the warm #FFF2EB used elsewhere: at 11.5px these
        // labels need 4.5:1 and that off-white only reaches 4.23 on this band.
        // White gives 4.64, and matches the other redesigned tables.
        color: "#fff",
        fontWeight: 600,
        fontSize: 11.5,
        letterSpacing: "0.06em",
        textTransform: "uppercase",
        whiteSpace: "nowrap",
        border: "none",
        padding: "13px 14px",
    },
}))(TableCell);

const StyledBodyRow = withStyles(() => ({
    root: {
        transition: "background-color 0.15s ease",
        "&:nth-of-type(even)": { backgroundColor: "#FBFCFE" },
        "&:hover": { backgroundColor: "#F1F6FD" },
    },
}))(TableRow);

const VERDICT_META = {
    real: { label: "Real", color: GOOD },
    hold: { label: "Hold", color: HOLD },
    junk: { label: "Junk", color: CRITICAL },
};

const verdictOf = (entry) => {
    if (entry.verdict === "junk") return "junk";
    if (entry.verdict === "hold") return "hold";
    return "real";
};

// /location, /experience-center and /location-expCenters all answer 404 with
// a {message} body when their collection is empty, rather than an empty array
// - and axios rejects on 404. An empty result is not a failure, so it comes
// back as []. Only a genuine error returns null.
const listOrNull = (settled) => {
    if (settled.status === "fulfilled") {
        return Array.isArray(settled.value?.data) ? settled.value.data : [];
    }
    if (settled.reason?.response?.status === 404) return [];
    return null;
};

// A stable signature of a filter set. Ids are sorted, so re-picking the same
// two cities in a different order is not treated as a change.
const filterKey = (f) => JSON.stringify({
    startDate: f.startDate,
    endDate: f.endDate,
    city: f.city.map((c) => c._id).sort(),
    experienceCenterId: f.experienceCenterId.map((e) => e._id).sort(),
});

const EMPTY_FILTERS = {
    startDate: moment().subtract(30, "days").format("YYYY-MM-DD"),
    endDate: moment().format("YYYY-MM-DD"),
    // Objects, not ids: the Autocomplete needs the whole option to render its
    // chip, and the ids are taken on the way out to the server.
    city: [],
    experienceCenterId: [],
};

const useStyles = makeStyles(() => ({
    pageRoot: {
        background: "linear-gradient(180deg, #F7F9FC 0%, #EFF3F9 100%)",
        minHeight: "100vh",
    },
    pageHeaderBanner: {
        position: "relative",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        gap: 16,
        padding: "20px 26px",
        borderRadius: 18,
        marginBottom: 14,
        background: HEADER_GRADIENT,
        boxShadow: "0 12px 30px rgba(193,83,31,0.26)",
        // Soft light bloom in the top-right corner so the banner reads as lit
        // rather than a flat gradient block.
        "&::after": {
            content: '""',
            position: "absolute",
            top: -80,
            right: -30,
            width: 260,
            height: 260,
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 70%)",
            pointerEvents: "none",
        },
        "& > *": { position: "relative", zIndex: 1 },
    },
    pageHeaderIconWrap: {
        width: 50,
        height: 50,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
        background: "rgba(255,255,255,0.16)",
        border: "2px solid rgba(255,255,255,0.3)",
        color: "#fff",
    },
    // textAlign is explicit because the page root carries CRA's `App` class,
    // and App.css sets `.App { text-align: center }` - inherited, that centred
    // the banner copy inside its stretched flex column.
    pageHeaderTitle: {
        fontSize: 20,
        fontWeight: 700,
        color: "#fff",
        letterSpacing: 0.2,
        textAlign: "left",
    },
    pageHeaderSubtitle: {
        fontSize: 13,
        color: "rgba(255,255,255,0.75)",
        marginTop: 4,
        textAlign: "left",
    },
    pageHeaderChip: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 16px",
        borderRadius: 20,
        background: "rgba(255,255,255,0.16)",
        color: "#fff",
        fontWeight: 700,
        fontSize: 13,
        whiteSpace: "nowrap",
        flexShrink: 0,
    },
    // A row, not a column: the "Updated …" pill sits directly beside the
    // Refresh button rather than stacked under it, so the control and the
    // freshness it reports read as one unit.
    pageHeaderRight: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        flexShrink: 0,
    },
    // Darker than pageHeaderChip on purpose: this sits toward the bright
    // #ff9c66 end of the gradient, where a translucent-white chip washes out.
    pageHeaderDateChip: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 13px",
        borderRadius: 20,
        background: "rgba(0,0,0,0.26)",
        color: "#fff",
        fontWeight: 700,
        fontSize: 12,
        whiteSpace: "nowrap",
        flexShrink: 0,
    },
    "@keyframes spin": {
        from: { transform: "rotate(0deg)" },
        to: { transform: "rotate(360deg)" },
    },
    "@keyframes pulseDot": {
        "0%, 100%": { opacity: 1, transform: "scale(1)" },
        "50%": { opacity: 0.45, transform: "scale(0.82)" },
    },
    // Was a translucent icon-only button, which all but disappeared against the
    // bright end of the gradient. Solid white with a dark ring: the ring is what
    // defines the EDGE, since white-on-#ff9c66 is only about 2:1. The word
    // "Refresh" is what makes the control findable at all.
    refreshButton: {
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "8px 16px",
        borderRadius: 999,
        border: "none",
        background: "#fff",
        color: ORANGE_DARK,
        fontSize: 12.5,
        fontWeight: 800,
        letterSpacing: 0.2,
        fontFamily: "inherit",
        cursor: "pointer",
        boxShadow: "0 0 0 1px rgba(0,0,0,0.14), 0 3px 10px rgba(0,0,0,0.24)",
        transition: "background .18s ease, transform .18s ease, box-shadow .18s ease",
        "&:hover:not(:disabled)": {
            background: "#FFF4ED",
            transform: "translateY(-1px)",
            boxShadow: "0 0 0 1px rgba(0,0,0,0.18), 0 6px 16px rgba(0,0,0,0.3)",
        },
        "&:active:not(:disabled)": { transform: "translateY(0)" },
        "&:disabled": { cursor: "wait", opacity: 0.85 },
    },
    refreshSpinning: {
        animation: "$spin 0.9s linear infinite",
    },
    lastUpdatedText: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        fontWeight: 600,
        color: "#fff",
        background: "rgba(0,0,0,0.42)",
        padding: "3px 10px",
        borderRadius: 999,
        whiteSpace: "nowrap",
    },
    liveDot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        flexShrink: 0,
        background: "#FFD2B5",
        animation: "$pulseDot 2s ease-in-out infinite",
    },
    filterPaper: {
        padding: "16px 20px",
        marginBottom: 14,
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
    },
    filterHeaderRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 14,
    },
    filterHeader: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        color: ORANGE_DARK,
        fontWeight: 600,
        fontSize: 15,
    },
    filterLoadingBadge: {
        display: "flex",
        alignItems: "center",
        gap: 7,
        padding: "4px 12px",
        borderRadius: 999,
        background: ORANGE_LIGHT,
        color: ORANGE_DARK,
        fontSize: 12,
        fontWeight: 700,
    },
    // A grid, not wrapping flex. Flex with `flex-end` lined the controls up by
    // their BOTTOM edge, so the one field carrying helper text sat a line
    // higher than the rest, and a wrap dropped the buttons into a ragged
    // position. Fixed tracks for the dates, elastic ones for the two pickers,
    // and the buttons in a track of their own.
    // Flex with `center`, not `flex-end`. Lining the controls up by their
    // BOTTOM edge meant the one field carrying helper text sat a line higher
    // than the rest; with every control the same height, centre is exact.
    // Fixed widths rather than elastic tracks - a filter does not get more
    // useful by being wider, and stretched-out pickers dwarf the card.
    // Flex with `center`, not `flex-end`. Lining the controls up by their
    // BOTTOM edge meant the one field carrying helper text sat a line higher
    // than the rest; with every control the same height, centre is exact.
    filterRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 12,
        transition: "opacity 0.2s ease",
    },
    // The two dates read as ONE control - they are one range - so they sit in
    // a shared bordered group with the dash between them, instead of as two
    // unrelated boxes that happen to be adjacent.
    dateGroup: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "7px 12px 7px 14px",
        borderRadius: 12,
        border: "1px solid #dfe5ef",
        background: "#fff",
        flexShrink: 0,
        // The whole group opens a picker, so it has to look clickable - a
        // text cursor over the label would say the opposite.
        cursor: "pointer",
        transition: "border-color 150ms ease, box-shadow 150ms ease",
        "&:hover": { borderColor: "#c3ccd9" },
        "&:focus-within": { borderColor: ORANGE, boxShadow: `0 0 0 3px ${ORANGE}1f` },
    },
    dateGroupLabel: {
        fontSize: 10.5,
        fontWeight: 800,
        letterSpacing: 0.6,
        textTransform: "uppercase",
        color: "#9aa4b4",
        marginRight: 2,
        whiteSpace: "nowrap",
    },
    // Underline-free, borderless inputs - the GROUP carries the border, so an
    // outlined box inside it would be a border inside a border.
    dateInput: {
        width: 124,
        "& input": {
            fontSize: 13,
            fontWeight: 600,
            color: INK,
            padding: "5px 0",
            cursor: "pointer",
        },
        "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" },
    },
    dateDash: { color: "#c3ccd9", fontSize: 13, fontWeight: 700, userSelect: "none" },
    cityField: { width: 186, flexShrink: 0 },
    ecField: { width: 228, flexShrink: 0 },
    filterActions: { display: "flex", alignItems: "center", gap: 8, marginLeft: "auto", flexShrink: 0 },
    "@media (max-width: 839.95px)": {
        // Once it wraps, "push right" would strand the buttons mid-row.
        filterActions: { marginLeft: 0, width: "100%", justifyContent: "flex-end" },
    },

    // ---- "you changed something, press Apply" -------------------------------
    // The filters are not live: nothing moves until Apply runs. Without this,
    // a changed dropdown and unchanged numbers look like a broken page.
    dirtyBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 12px",
        borderRadius: 999,
        background: "#fff4e8",
        border: `1px solid ${ORANGE}66`,
        color: "#9c4a16",
        fontSize: 11.5,
        fontWeight: 800,
        whiteSpace: "nowrap",
    },
    dirtyDot: {
        width: 6,
        height: 6,
        borderRadius: "50%",
        background: ORANGE_DARK,
        animation: "$pulseDot 1.6s ease-in-out infinite",
    },
    // Draws the eye to the one button that will act on the change.
    applyButtonDirty: {
        animation: "$nudge 1.8s ease-in-out infinite",
    },
    "@keyframes nudge": {
        "0%, 100%": { boxShadow: "0 4px 10px rgba(193,83,31,0.32)" },
        "50%": { boxShadow: `0 4px 10px rgba(193,83,31,0.32), 0 0 0 6px ${ORANGE}26` },
    },
    // The count that used to be the Experience Center field's helperText.
    // Below the row, so it cannot push one control out of line with the rest.
    filterHint: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        marginTop: 10,
        minHeight: 16,
        fontSize: 11.5,
        fontWeight: 600,
        color: "#8b7a6f",
    },
    panelCard: {
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
        height: "100%",
        transition: "box-shadow 0.25s ease",
        "&:hover": { boxShadow: "0 10px 28px rgba(28,55,93,0.11)" },
    },
    panelTitle: {
        display: "flex",
        alignItems: "center",
        gap: 9,
        color: "#1f2d3d",
        fontWeight: 700,
        fontSize: 15,
        marginBottom: 3,
        "&::before": {
            content: '""',
            width: 4,
            height: 16,
            borderRadius: 3,
            flexShrink: 0,
            background: `linear-gradient(180deg, ${ORANGE}, ${ORANGE_DARK})`,
        },
    },
    panelHint: {
        color: "#8b93a6",
        fontSize: 12.5,
        marginBottom: 8,
        paddingLeft: 13,
    },
    tableHint: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        color: "#6b7689",
        fontSize: 12,
        marginBottom: 12,
        padding: "8px 12px",
        background: "#F7F9FC",
        border: "1px solid #eaeff6",
        borderRadius: 9,
    },
    tableCard: {
        borderRadius: 16,
        border: "1px solid #eaeff6",
        boxShadow: "0 2px 16px rgba(28,55,93,0.06)",
    },
    tableHeaderRow: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 10,
        marginBottom: 14,
        transition: "opacity 0.2s ease",
    },
    chipGroup: {
        display: "flex",
        gap: 8,
    },
    filterChip: {
        cursor: "pointer",
        fontWeight: 600,
    },
    field: {
        "& .MuiOutlinedInput-root": { borderRadius: 10 },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
        "& .MuiInputLabel-root.Mui-focused": { color: ORANGE_DARK },
    },
    emptyState: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: "34px 20px",
        color: "#9aa4b4",
        textAlign: "center",
    },
    emptyIcon: {
        fontSize: 32,
        color: "#c7cedb",
        marginBottom: 6,
    },
    applyButton: {
        borderRadius: "20px !important",
        fontWeight: 600,
        display: "inline-flex !important",
        alignItems: "center !important",
        color: "#fff !important",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE}) !important`,
        borderColor: "transparent !important",
        boxShadow: "0 4px 10px rgba(193,83,31,0.32) !important",
    },
    clearButton: {
        borderRadius: "20px !important",
        fontWeight: 500,
        display: "inline-flex !important",
        alignItems: "center !important",
    },
    downloadButton: {
        borderRadius: "20px !important",
        fontWeight: 600,
        display: "inline-flex !important",
        alignItems: "center !important",
        color: "#fff !important",
        background: `${GREEN_GRADIENT} !important`,
        borderColor: "transparent !important",
        boxShadow: "0 4px 10px rgba(20,108,67,0.32) !important",
        transition: "box-shadow 0.2s ease, transform 0.2s ease",
        "&:hover": {
            boxShadow: "0 6px 16px rgba(20,108,67,0.42) !important",
            transform: "translateY(-1px)",
        },
    },
    searchField: {
        "& .MuiOutlinedInput-root": {
            borderRadius: 999,
            backgroundColor: "#fff",
        },
        "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: ORANGE },
    },
    searchClearBtn: {
        padding: 4,
        color: "#9aa4b4",
    },
    detailWrap: {
        background: "#F7F9FC",
        borderTop: "1px solid #eaeff6",
        padding: "16px 24px 20px 56px",
    },
    detailBlock: {
        background: "#fff",
        border: "1px solid #eaeff6",
        borderRadius: 10,
        padding: "12px 16px",
        marginBottom: 12,
        boxShadow: "0 1px 4px rgba(28,55,93,0.05)",
    },
    leadLink: {
        display: "inline-flex",
        alignItems: "center",
        padding: "3px 10px",
        borderRadius: 7,
        background: "#EAF2FD",
        color: BLUE,
        fontWeight: 700,
        fontSize: 12.5,
        cursor: "pointer",
        transition: "background-color 0.15s ease",
        "&:hover": { background: "#d7e6fa" },
    },
    detailLabel: {
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 11,
        textTransform: "uppercase",
        letterSpacing: "0.04em",
        color: "#8b93a6",
        fontWeight: 700,
        marginBottom: 7,
    },
    legendList: {
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginTop: 16,
    },
    // The row's background is filled proportionally to the verdict's share
    // (set inline), so the legend doubles as a mini bar chart.
    legendRow: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 13px",
        borderRadius: 11,
        border: "1px solid #eaeff6",
        transition: "transform 0.15s ease, box-shadow 0.15s ease",
        "&:hover": {
            transform: "translateX(2px)",
            boxShadow: "0 3px 10px rgba(28,55,93,0.09)",
        },
    },
    legendDot: {
        width: 11,
        height: 11,
        borderRadius: "50%",
        flexShrink: 0,
    },
    legendLabel: {
        fontSize: 13,
        fontWeight: 600,
        color: "#4a5568",
        flex: 1,
    },
    legendValue: {
        fontSize: 14,
        fontWeight: 700,
        color: ORANGE_DARK,
    },
    legendPct: {
        fontSize: 11.5,
        color: "#8b93a6",
        minWidth: 36,
        textAlign: "right",
        fontWeight: 600,
    },
    excludedNote: {
        fontSize: 11.5,
        color: "#9aa4b4",
        marginTop: 12,
        lineHeight: 1.5,
    },
    // ── Unique/All view switch ───────────────────────────────────────────
    // A segmented control rather than a plain switch: each side carries its
    // own row count, so the drop from "67 reviews" to "40 leads" explains
    // itself instead of looking like rows went missing.
    viewToggle: {
        display: "inline-flex",
        alignItems: "center",
        padding: 3,
        borderRadius: 999,
        background: "#EEF2F8",
        border: "1px solid #e1e8f2",
        gap: 2,
    },
    viewToggleBtn: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        border: "none",
        cursor: "pointer",
        borderRadius: 999,
        padding: "6px 14px",
        fontSize: 12.5,
        fontWeight: 700,
        fontFamily: "inherit",
        color: "#66718a",
        background: "transparent",
        transition: "color 0.18s ease, background 0.18s ease, box-shadow 0.18s ease",
        "&:hover:not($viewToggleBtnActive):not(:disabled)": { color: ORANGE_DARK },
        "&:disabled": { cursor: "not-allowed", opacity: 0.6 },
    },
    viewToggleBtnActive: {
        color: "#fff",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
        boxShadow: "0 3px 9px rgba(193,83,31,0.32)",
    },
    viewToggleCount: {
        fontSize: 11,
        fontWeight: 800,
        padding: "1px 7px",
        borderRadius: 999,
        background: "rgba(0,0,0,0.07)",
    },
    viewToggleCountActive: {
        background: "rgba(255,255,255,0.24)",
    },
    // Strip of re-review figures, only meaningful once duplicates collapse.
    uniqueInsightBar: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 10,
        padding: "10px 14px",
        marginBottom: 12,
        borderRadius: 11,
        background: "linear-gradient(135deg, #FFF8F3 0%, #FFF1EA 100%)",
        border: `1px solid ${ORANGE}33`,
    },
    insightPill: {
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "5px 12px",
        borderRadius: 999,
        background: "#fff",
        border: "1px solid #f0d9cc",
        fontSize: 12,
        color: "#5c6779",
        fontWeight: 600,
    },
    insightPillValue: {
        fontWeight: 800,
        color: ORANGE_DARK,
        fontSize: 13,
    },
    // "×3" on a row that was reviewed more than once.
    reviewCountBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: 3,
        marginLeft: 7,
        padding: "1px 7px",
        borderRadius: 999,
        background: ORANGE_LIGHT,
        border: `1px solid ${ORANGE}44`,
        color: ORANGE_DARK,
        fontSize: 10.5,
        fontWeight: 800,
        verticalAlign: "middle",
    },
    changedBadge: {
        display: "inline-flex",
        alignItems: "center",
        gap: 4,
        marginLeft: 6,
        padding: "1px 7px",
        borderRadius: 999,
        background: "#FEF3F2",
        border: "1px solid #f5c2c0",
        color: CRITICAL,
        fontSize: 10.5,
        fontWeight: 800,
        verticalAlign: "middle",
    },
    // ── Review history timeline (expanded row, unique mode) ──────────────
    timeline: {
        position: "relative",
        paddingLeft: 26,
        // The connecting spine behind the dots.
        "&::before": {
            content: '""',
            position: "absolute",
            left: 7,
            top: 6,
            bottom: 6,
            width: 2,
            borderRadius: 2,
            background: "linear-gradient(180deg, #dbe3ee, #eef2f8)",
        },
    },
    timelineItem: {
        position: "relative",
        marginBottom: 14,
        "&:last-child": { marginBottom: 0 },
    },
    timelineDot: {
        position: "absolute",
        left: -26,
        top: 4,
        width: 16,
        height: 16,
        borderRadius: "50%",
        border: "3px solid #fff",
        boxShadow: "0 0 0 1.5px #dbe3ee",
    },
    timelineHead: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 8,
        marginBottom: 7,
    },
    timelineIndex: {
        fontSize: 11,
        fontWeight: 800,
        letterSpacing: "0.04em",
        textTransform: "uppercase",
        color: "#8b93a6",
    },
    timelineDate: {
        fontSize: 11.5,
        color: "#9aa4b4",
        fontWeight: 600,
    },
    latestTag: {
        fontSize: 9.5,
        fontWeight: 800,
        letterSpacing: "0.05em",
        textTransform: "uppercase",
        padding: "1px 7px",
        borderRadius: 999,
        background: "#EAF2FD",
        color: BLUE,
        border: "1px solid #cfe0f7",
    },
    // ── Loading indicators ───────────────────────────────────────────────
    // Sits flush inside the table card's rounded top edge.
    tableProgressSlot: {
        height: 4,
        marginBottom: 8,
        borderRadius: 999,
        overflow: "hidden",
    },
    loadingBanner: {
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "9px 14px",
        marginBottom: 14,
        borderRadius: 12,
        background: ORANGE_LIGHT,
        border: `1px solid ${ORANGE}33`,
    },
    loadingBannerTitle: {
        fontSize: 13,
        fontWeight: 700,
        color: ORANGE_DARK,
    },
    loadingBannerHint: {
        fontSize: 11.5,
        color: "#7a6a60",
        marginTop: 1,
    },
    skeletonCell: {
        ...cellBorder,
        // Keeps skeleton rows the same height as real ones so the table does
        // not visibly resize when the data lands.
        height: 53,
    },
}));

const EmptyState = ({ classes, message }) => (
    <div className={classes.emptyState}>
        <InboxOutlinedIcon className={classes.emptyIcon} />
        <div style={{ fontSize: 13 }}>{message}</div>
    </div>
);

// Placeholder rows shaped like real ones, instead of a single centred spinner.
// The table keeps its height and column rhythm while loading, so the layout
// doesn't jump when the data arrives - and the wait reads as "nearly there"
// rather than "nothing here".
const SkeletonRows = ({ classes, rows = 6 }) => (
    <>
        {Array.from({ length: rows }).map((_, i) => (
            <TableRow key={i}>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="circle" width={22} height={22} />
                </TableCell>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="rect" width={62} height={20} style={{ borderRadius: 7 }} />
                </TableCell>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="text" width="72%" />
                    <Skeleton variant="text" width="45%" height={11} />
                </TableCell>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="text" width="80%" />
                </TableCell>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="rect" width={54} height={22} style={{ borderRadius: 999 }} />
                </TableCell>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="text" width={96} />
                </TableCell>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="text" width={82} />
                </TableCell>
                <TableCell className={classes.skeletonCell}>
                    <Skeleton variant="text" width={82} />
                </TableCell>
            </TableRow>
        ))}
    </>
);

// One review's note + analysis + tips. Rendered on its own for a single
// review, or repeated down the timeline when a lead was reviewed several times.
const ReviewBody = ({ review, classes }) => (
    <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
            {review.latestNote ? (
                <div className={classes.detailBlock} style={{ borderLeft: `3px solid #94a3b8` }}>
                    <div className={classes.detailLabel}>
                        <RecordVoiceOverIcon style={{ fontSize: 14 }} />
                        Sales note on the call
                    </div>
                    <div style={{ lineHeight: 1.55 }}>{review.latestNote}</div>
                </div>
            ) : null}
            <div className={classes.detailBlock} style={{ borderLeft: `3px solid ${ORANGE}`, marginBottom: 0 }}>
                <div className={classes.detailLabel}>
                    <MemoryOutlinedIcon style={{ fontSize: 14 }} />
                    AI analysis
                </div>
                {review.aiAnalysis ? (
                    <div style={{ lineHeight: 1.55 }}>{review.aiAnalysis}</div>
                ) : (
                    <div style={{ color: "#8b93a6" }}>No reasoning returned.</div>
                )}
            </div>
        </Grid>
        <Grid item xs={12} md={6}>
            <div className={classes.detailBlock} style={{ borderLeft: `3px solid ${GOOD}`, marginBottom: 0 }}>
                <div className={classes.detailLabel}>
                    <EmojiObjectsOutlinedIcon style={{ fontSize: 14 }} />
                    Sales tips
                </div>
                {review.salesTips && review.salesTips.length ? (
                    <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.7 }}>
                        {review.salesTips.map((tip, i) => (
                            <li key={i}>{tip}</li>
                        ))}
                    </ul>
                ) : (
                    <div style={{ color: "#8b93a6" }}>None for this review.</div>
                )}
            </div>
        </Grid>
    </Grid>
);

const DetailRow = ({ entry, classes }) => {
    // `history` only exists on unique-mode rows. With a single review there is
    // nothing to lay out chronologically, so it renders exactly as before -
    // the timeline would just be a lone dot.
    const history = Array.isArray(entry.history) ? entry.history : null;
    const isMulti = history && history.length > 1;

    return (
        <TableRow>
            <TableCell style={{ ...cellBorder, padding: 0 }} colSpan={9}>
                <Collapse in timeout="auto" unmountOnExit>
                    <div className={classes.detailWrap}>
                        {isMulti ? (
                            <>
                                <div className={classes.detailLabel} style={{ marginBottom: 12 }}>
                                    <HistoryOutlinedIcon style={{ fontSize: 15 }} />
                                    {`All ${history.length} AI reviews for this lead — newest first`}
                                    {entry.verdictChanged ? (
                                        <span className={classes.changedBadge} style={{ marginLeft: 2 }}>
                                            <CompareArrowsOutlinedIcon style={{ fontSize: 11 }} />
                                            Verdict changed
                                        </span>
                                    ) : null}
                                </div>
                                <div className={classes.timeline}>
                                    {history.map((review, i) => {
                                        const meta = VERDICT_META[verdictOf(review)];
                                        return (
                                            <div key={review.id || i} className={classes.timelineItem}>
                                                <span
                                                    className={classes.timelineDot}
                                                    style={{ backgroundColor: meta.color }}
                                                />
                                                <div className={classes.timelineHead}>
                                                    <span className={classes.timelineIndex}>
                                                        {`Review ${history.length - i} of ${history.length}`}
                                                    </span>
                                                    <Chip
                                                        size="small"
                                                        label={meta.label}
                                                        style={{
                                                            height: 20,
                                                            backgroundColor: `${meta.color}16`,
                                                            color: meta.color,
                                                            fontWeight: 700,
                                                            fontSize: 11,
                                                            border: `1px solid ${meta.color}33`,
                                                        }}
                                                    />
                                                    <span className={classes.timelineDate}>
                                                        {moment(review.createdAt).format("DD MMM YYYY, hh:mm A")}
                                                    </span>
                                                    {review.reasonForReview ? (
                                                        <span className={classes.timelineDate}>· {review.reasonForReview}</span>
                                                    ) : null}
                                                    {i === 0 ? <span className={classes.latestTag}>Latest</span> : null}
                                                </div>
                                                <ReviewBody review={review} classes={classes} />
                                            </div>
                                        );
                                    })}
                                </div>
                            </>
                        ) : (
                            <ReviewBody review={history ? history[0] : entry} classes={classes} />
                        )}
                    </div>
                </Collapse>
            </TableCell>
        </TableRow>
    );
};

const MoveToMarketingClassifierDashboard = (props) => {
    const classes = useStyles();
    const [filters, setFilters] = useState(EMPTY_FILTERS);
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [verdictFilter, setVerdictFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [openRowId, setOpenRowId] = useState(null);
    // false = every classifier review (one row per review, the original view).
    // true  = one row per lead, keeping each lead's full review history.
    // Both are computed by the same request, so switching needs no refetch.
    const [uniqueMode, setUniqueMode] = useState(false);
    const [lastUpdated, setLastUpdated] = useState(null);
    // "Updated 4 minutes ago" is only true at the instant it renders, so a slow
    // tick re-renders it. 30s is plenty - moment rounds to the minute anyway.
    const [nowTick, setNowTick] = useState(Date.now());
    useEffect(() => {
        const id = setInterval(() => setNowTick(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);
    // The range the data on screen was actually fetched with. Editing a date in
    // the filter panel must not change the header chip until Apply has run,
    // otherwise the banner describes a range the numbers do not cover.
    const [appliedRange, setAppliedRange] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // ---- city / experience-centre scope -------------------------------------
    // `allExperienceCenters` is the unfiltered list, kept so clearing the city
    // can put the full set back without another request.
    const [cities, setCities] = useState([]);
    const [allExperienceCenters, setAllExperienceCenters] = useState([]);
    const [experienceCenters, setExperienceCenters] = useState([]);
    const [optionsLoading, setOptionsLoading] = useState(false);
    const [ecLoading, setEcLoading] = useState(false);
    // Native date inputs only open their calendar when the little icon is hit,
    // which is a 20px target inside a much larger control. These let the whole
    // group act as the trigger.
    const startDateRef = useRef(null);
    const endDateRef = useRef(null);
    const openPicker = (ref) => {
        const input = ref.current;
        if (!input || input.disabled) return;
        input.focus();
        // showPicker arrived in Chrome/Edge 99 and still is not in Safari, and
        // it throws if the call is not inside a user gesture - so the focus
        // above is what makes this degrade cleanly rather than do nothing.
        try {
            if (typeof input.showPicker === "function") input.showPicker();
        } catch (e) { /* focus is already the fallback */ }
    };

    // The scope the figures on screen were fetched with - same reason as
    // appliedRange: editing a filter must not relabel data it has not touched.
    const [appliedScope, setAppliedScope] = useState({ city: [], experienceCenters: [] });
    // A signature of the filters the data was fetched with. Compared against
    // the live ones to tell whether what is on screen still matches the
    // controls above it.
    const [appliedKey, setAppliedKey] = useState(null);

    const loadFilterOptions = useCallback(async () => {
        setOptionsLoading(true);
        // Settled, not all: one list failing should not leave the other empty.
        const [locationRes, ecRes] = await Promise.allSettled([
            HttpService.getLocation(),
            HttpService.getExperinceCenter(),
        ]);

        const cityList = listOrNull(locationRes);
        if (cityList) setCities(cityList);
        else props.setSnackInfo("Could not load the city list", "error");

        const ecList = listOrNull(ecRes);
        if (ecList) {
            setAllExperienceCenters(ecList);
            setExperienceCenters(ecList);
        } else {
            props.setSnackInfo("Could not load the experience centre list", "error");
        }
        setOptionsLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Picking a city narrows the centre list to that city's centres, and drops
    // any centre already chosen - keeping them would leave a selection the
    // list no longer offers, which then silently scopes the data to a centre
    // the user can no longer see.
    const handleCityChange = async (event, selectedCities) => {
        setFilters((prev) => ({ ...prev, city: selectedCities, experienceCenterId: [] }));

        if (!selectedCities.length) {
            setExperienceCenters(allExperienceCenters);
            return;
        }
        setEcLoading(true);
        // allSettled, not all: /location-expCenters answers 404 when a city
        // has no centres, and Promise.all would throw that away along with
        // every other city's centres. A city with none is an empty list, not
        // a failed request.
        const results = await Promise.allSettled(
            selectedCities.map((city) => HttpService.getExperienceCentersByCity(city._id)),
        );
        const merged = [];
        let failed = 0;
        results.forEach((result) => {
            const list = listOrNull(result);
            if (list) merged.push(...list);
            else failed += 1;
        });
        // Two cities can share a centre in the data; de-duped by id so it
        // cannot appear twice in the list.
        setExperienceCenters(Array.from(new Map(merged.map((ec) => [ec._id, ec])).values()));
        if (failed) {
            props.setSnackInfo(
                `Could not load the experience centres for ${failed} of the selected ${failed === 1 ? "city" : "cities"}`,
                "error",
            );
        }
        setEcLoading(false);
    };

    // `override` lets a caller fetch with filters that state has not caught up
    // to yet - Clear sets the filters and fetches in the same tick, and the
    // closure here would otherwise still hold the pre-Clear values.
    //
    // Never passed straight to onClick: the click event would arrive as the
    // override and be read as a filter set.
    const fetchDashboard = useCallback(async (override) => {
        const active = override || filters;
        setLoading(true);
        try {
            const params = {
                startDate: active.startDate,
                endDate: active.endDate,
            };
            // Omitted entirely when nothing is picked, so an empty selection
            // never reaches the server as an empty string meaning "no centre".
            if (active.city.length) params.city = active.city.map((c) => c._id).join(",");
            if (active.experienceCenterId.length) {
                params.experienceCenterId = active.experienceCenterId.map((e) => e._id).join(",");
            }

            const response = await HttpService.getMoveToMarketingClassifierDashboard(params);
            setData(response.data);
            setLastUpdated(new Date());
            setAppliedRange({ startDate: active.startDate, endDate: active.endDate });
            setAppliedScope({
                city: active.city.map((c) => c.name).filter(Boolean),
                experienceCenters: active.experienceCenterId.map((e) => e.name || e.code).filter(Boolean),
            });
            setAppliedKey(filterKey(active));
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message ?? "Failed to load the AI review dashboard", "error");
        }
        setLoading(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    useEffect(() => {
        loadFilterOptions();
        fetchDashboard();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Whenever the underlying data or the on-screen filters change, the old
    // page number can point past the end of the new (usually shorter) result
    // set - reset to the first page so the table never renders empty.
    useEffect(() => {
        setPage(0);
    }, [data, verdictFilter, search, uniqueMode]);

    // Switching view can drop the row that was expanded (or change what its
    // panel should show), so collapse rather than leave a stale panel open.
    useEffect(() => {
        setOpenRowId(null);
    }, [uniqueMode]);

    // Clears the search box too - leaving it populated meant "Clear" reset the
    // dates but the table still showed a filtered subset, which reads as the
    // reset not having worked.
    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setSearch("");
        // The centre list was narrowed by the city that just went away, so put
        // the full set back - otherwise Clear leaves a list that still only
        // offers one city's centres.
        setExperienceCenters(allExperienceCenters);
        // Clear is an action, not an edit: it says "show me everything", so it
        // applies itself. Leaving it to prompt for Apply would mean two clicks
        // to undo one. The cleared set is passed explicitly because `filters`
        // in this closure is still the pre-Clear value.
        fetchDashboard(EMPTY_FILTERS);
    };

    const allEntries = data?.entries || [];
    const uniqueEntriesData = data?.uniqueEntries || [];
    // An older backend returns only `summary`/`entries`. Without this the
    // unique view would render a convincing but empty table instead of making
    // it obvious that the server simply hasn't been updated yet.
    const supportsUnique = Boolean(data?.uniqueSummary);

    // Everything below - table, donut, reason bars, Excel - reads these two, so
    // the whole page swaps together and the chart can never disagree with the
    // row count underneath it.
    const summary = uniqueMode ? data?.uniqueSummary : data?.summary;
    const entries = uniqueMode ? uniqueEntriesData : allEntries;

    const verdictCounts = {
        real: summary?.realCount || 0,
        hold: summary?.holdCount || 0,
        junk: summary?.junkCount || 0,
    };
    const verdictTotal = verdictCounts.real + verdictCounts.hold + verdictCounts.junk;
    const hasVerdictData = verdictTotal > 0;
    const donutData = {
        labels: [VERDICT_META.real.label, VERDICT_META.hold.label, VERDICT_META.junk.label],
        datasets: [
            {
                data: [verdictCounts.real, verdictCounts.hold, verdictCounts.junk],
                backgroundColor: [DONUT_COLORS.real, DONUT_COLORS.hold, DONUT_COLORS.junk],
                borderWidth: 3,
                borderColor: "#fff",
                hoverOffset: 8,
            },
        ],
    };

    const reasonEntries = Object.entries(summary?.byReason || {}).sort((a, b) => b[1] - a[1]).slice(0, 7);
    const reasonChartData = {
        labels: reasonEntries.map(([name]) => name),
        datasets: [
            {
                label: "Reviews",
                data: reasonEntries.map(([, n]) => n),
                backgroundColor: BLUE,
                hoverBackgroundColor: "#1c5fae",
                borderRadius: 5,
                maxBarThickness: 26,
            },
        ],
    };

    let filteredEntries = entries;
    if (verdictFilter !== "all") {
        filteredEntries = filteredEntries.filter((e) => verdictOf(e) === verdictFilter);
    }
    if (search.trim()) {
        const term = search.trim().toLowerCase();
        filteredEntries = filteredEntries.filter(
            (e) =>
                String(e.lead_no || "").includes(term) ||
                (e.customerName || "").toLowerCase().includes(term) ||
                (e.reasonForReview || "").toLowerCase().includes(term)
        );
    }

    const paginatedEntries = filteredEntries.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    // Exports every row matching the current filters/search - not just the
    // page currently on screen - including every field, such as the AI's
    // full reasoning and sales tips, which the table only shows a peek of.
    const downloadExcel = () => {
        if (!filteredEntries.length) {
            props.setSnackInfo("No data available to download", "info");
            return;
        }

        const rows = filteredEntries.map((entry, i) => {
            const row = {
                "Sl. No.": i + 1,
                "Lead No.": entry.lead_no ?? "-",
                "Unique Lead No.": entry.uniqueLeadNumber ?? "-",
                "Client Name": entry.customerName || "-",
                "Current Stage": entry.currentStage || "-",
                // Two columns here, unlike the table's single stacked cell:
                // a spreadsheet sorts, filters and pivots on one column at a
                // time, and "EC Koramangala / Bangalore" in one cell does none
                // of that. City is the centre's own location.
                "Experience Center": entry.experienceCenter || "-",
                "City": entry.city || "-",
                "Reason for Review": entry.reasonForReview || "-",
                "Verdict": VERDICT_META[verdictOf(entry)].label,
                "AI Analysis": entry.aiAnalysis || "-",
                "Sales Tips": entry.salesTips && entry.salesTips.length ? entry.salesTips.join(" | ") : "-",
                "Sales Note on Call": entry.latestNote || "-",
                "Reviewed On": entry.createdAt ? moment(entry.createdAt).format("DD-MM-YYYY hh:mm A") : "-",
                "Lead Assigned Date": entry.leadAssignedDate ? moment(entry.leadAssignedDate).format("DD-MM-YYYY") : "-",
                "Created Date": entry.leadCreatedAt ? moment(entry.leadCreatedAt).format("DD-MM-YYYY") : "-",
            };

            if (!uniqueMode) return row;

            // In unique mode the columns above describe the LATEST review only,
            // so the earlier ones would silently vanish from the export. Flatten
            // the whole history into one cell (oldest first, each stamped with
            // its date and verdict) so the spreadsheet carries everything the
            // expanded row shows.
            const history = Array.isArray(entry.history) ? entry.history : [];
            const ordered = [...history].reverse();
            return {
                ...row,
                "Verdict": `${VERDICT_META[verdictOf(entry)].label}${entry.verdictChanged ? " (changed)" : ""}`,
                "Total Reviews": entry.reviewCount || 1,
                "Verdict Path": ordered.map((h) => VERDICT_META[verdictOf(h)].label).join(" → ") || "-",
                "All AI Analyses": ordered.length
                    ? ordered
                        .map(
                            (h, idx) =>
                                `[Review ${idx + 1} · ${moment(h.createdAt).format("DD-MM-YYYY hh:mm A")} · ${VERDICT_META[verdictOf(h)].label
                                }] ${h.aiAnalysis || "No reasoning returned."}`
                        )
                        .join("\n\n")
                    : "-",
            };
        });

        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, uniqueMode ? "AI Review (Unique)" : "AI Review");
        XLSX.writeFile(
            wb,
            `Move_To_Marketing_AI_Review${uniqueMode ? "_Unique" : ""}_${moment().format("YYYYMMDD_HHmmss")}.xlsx`
        );
    };

    // The applied range, written the way people actually read it. A range that
    // sits inside one month collapses to "1 — 27 Sep 2026" rather than repeating
    // the month twice, and a whole calendar month is just its name.
    const rangeLabel = (() => {
        const range = appliedRange || filters;
        if (!range?.startDate || !range?.endDate) return "All dates";
        const from = moment(range.startDate);
        const to = moment(range.endDate);
        if (!from.isValid() || !to.isValid()) return "All dates";

        const wholeMonth =
            from.isSame(to, "month") &&
            from.isSame(from.clone().startOf("month"), "day") &&
            to.isSame(to.clone().endOf("month"), "day");
        if (wholeMonth) return from.format("MMMM YYYY");

        if (from.isSame(to, "day")) return from.format("DD MMM YYYY");
        if (from.isSame(to, "month")) return `${from.format("DD")} — ${to.format("DD MMM YYYY")}`;
        if (from.isSame(to, "year")) return `${from.format("DD MMM")} — ${to.format("DD MMM YYYY")}`;
        return `${from.format("DD MMM YYYY")} — ${to.format("DD MMM YYYY")}`;
    })();

    // "Bangalore · 2 centres", or the single name when there is only one of
    // it - a chip reading "1 city" tells the reader nothing they wanted.
    const scopeLabel = (() => {
        const parts = [];
        const { city = [], experienceCenters: ecs = [] } = appliedScope;
        if (city.length === 1) parts.push(city[0]);
        else if (city.length > 1) parts.push(`${city.length} cities`);
        if (ecs.length === 1) parts.push(ecs[0]);
        else if (ecs.length > 1) parts.push(`${ecs.length} centres`);
        return parts.join(" · ");
    })();
    const scopeTitle = [
        appliedScope.city.length ? `Cities: ${appliedScope.city.join(", ")}` : "",
        appliedScope.experienceCenters.length ? `Centres: ${appliedScope.experienceCenters.join(", ")}` : "",
    ].filter(Boolean).join(" — ");

    // The controls no longer describe what is on screen. Null on the very
    // first render (nothing fetched yet), which must NOT read as dirty.
    const isDirty = appliedKey !== null && appliedKey !== filterKey(filters);

    const rangeDays = (() => {
        const range = appliedRange || filters;
        if (!range?.startDate || !range?.endDate) return null;
        const days = moment(range.endDate).diff(moment(range.startDate), "days") + 1;
        return days > 0 ? days : null;
    })();

    return (
        <div className={`App ${classes.pageRoot}`} style={{ padding: "12px 16px 16px" }}>
            <div className={classes.pageHeaderBanner}>
                <div className={classes.pageHeaderIconWrap}>
                    <TrendingUpIcon style={{ fontSize: 26 }} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.pageHeaderTitle}>Move to Marketing — AI Review</div>
                    <div className={classes.pageHeaderSubtitle}>
                        AI-reviewed Runo calls for leads sent back to Marketing, with the verdict and reasoning behind each one.
                    </div>
                </div>
                {summary ? (
                    <div className={classes.pageHeaderChip}>
                        <PeopleAltIcon style={{ fontSize: 15 }} />
                        {`${summary.total ?? 0} ${uniqueMode ? "leads" : "reviewed"}`}
                    </div>
                ) : null}
                {/* Says which period the figures cover, right next to them,
                    instead of leaving it to be inferred from the filter panel. */}
                <div className={classes.pageHeaderDateChip}>
                    <DateRangeOutlinedIcon style={{ fontSize: 15 }} />
                    {rangeLabel}
                    {rangeDays && (
                        <span style={{ opacity: 0.75, fontWeight: 600 }}>{`· ${rangeDays}d`}</span>
                    )}
                </div>
                {/* Only when something is actually narrowed. A low total is
                    otherwise indistinguishable from a quiet month. */}
                {scopeLabel && (
                    <MuiTooltip title={scopeTitle}>
                        <div className={classes.pageHeaderDateChip}>
                            <PlaceOutlinedIcon style={{ fontSize: 15 }} />
                            {scopeLabel}
                        </div>
                    </MuiTooltip>
                )}
                <div className={classes.pageHeaderRight}>
                    <MuiTooltip
                        title={
                            lastUpdated
                                ? `Last pulled at ${moment(lastUpdated).format("DD MMM YYYY, hh:mm:ss A")} — click to fetch the latest reviews`
                                : "Fetch the latest reviews"
                        }
                    >
                        {/* A labelled button rather than an IconButton: the word
                            "Refresh" is what makes the control findable, and a
                            tooltip alone only helps people who already hovered.
                            The span is required because a disabled button fires no
                            mouse events, so the tooltip would die mid-refresh. */}
                        <span style={{ display: "inline-flex" }}>
                            <button
                                type="button"
                                className={classes.refreshButton}
                                onClick={() => fetchDashboard()}
                                disabled={loading}
                            >
                                <RefreshIcon
                                    className={loading ? classes.refreshSpinning : undefined}
                                    style={{ fontSize: 17 }}
                                />
                                {loading ? "Refreshing…" : "Refresh"}
                            </button>
                        </span>
                    </MuiTooltip>
                    {lastUpdated && (
                        <div className={classes.lastUpdatedText}>
                            <span className={classes.liveDot} />
                            {/* Relative beats "02:14:07 PM": it answers "is this
                                stale?" without the reader doing the arithmetic. */}
                            {loading ? "Fetching latest…" : `Updated ${moment(lastUpdated).from(nowTick)}`}
                        </div>
                    )}
                </div>
            </div>

            <Paper className={classes.filterPaper}>
                <div className={classes.filterHeaderRow}>
                    <div className={classes.filterHeader}>
                        <TuneIcon fontSize="small" />
                        Filters
                    </div>
                    {loading && (
                        <div className={classes.filterLoadingBadge}>
                            <CircularProgress size={12} thickness={5} style={{ color: ORANGE_DARK }} />
                            Fetching data, please wait…
                        </div>
                    )}
                </div>
                <div
                    className={classes.filterRow}
                    style={{ opacity: loading ? 0.55 : 1, pointerEvents: loading ? "none" : "auto" }}
                >
                    {/* One range, so one control. Two separate outlined boxes
                        read as two unrelated filters, and squeezing them to fit
                        made the dates themselves hard to read. */}
                    {/* The whole group is the trigger. A click on the label,
                        the arrow or the padding opens the START picker; a
                        click on either input opens that one's own. */}
                    <div
                        className={classes.dateGroup}
                        onClick={(event) => {
                            if (event.target.tagName !== "INPUT") openPicker(startDateRef);
                        }}
                    >
                        <span className={classes.dateGroupLabel}>Period</span>
                        <TextField
                            type="date"
                            size="small"
                            className={classes.dateInput}
                            inputRef={startDateRef}
                            inputProps={{ max: filters.endDate || undefined, "aria-label": "Start date" }}
                            value={filters.startDate}
                            onClick={() => openPicker(startDateRef)}
                            onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                            disabled={loading}
                        />
                        <span className={classes.dateDash}>→</span>
                        <TextField
                            type="date"
                            size="small"
                            className={classes.dateInput}
                            inputRef={endDateRef}
                            inputProps={{ min: filters.startDate || undefined, "aria-label": "End date" }}
                            value={filters.endDate}
                            onClick={() => openPicker(endDateRef)}
                            onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                            disabled={loading}
                        />
                    </div>
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        disabled={loading || optionsLoading}
                        value={filters.city}
                        onChange={handleCityChange}
                        options={cities}
                        getOptionLabel={(option) => option.name || ""}
                        getOptionSelected={(option, value) => option._id === value._id}
                        className={`${classes.field} ${classes.cityField}`}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="City"
                                variant="outlined"
                                placeholder={filters.city.length ? "" : "All cities"}
                            />
                        )}
                    />
                    <Autocomplete
                        multiple
                        size="small"
                        limitTags={1}
                        disabled={loading || optionsLoading || ecLoading}
                        loading={ecLoading}
                        value={filters.experienceCenterId}
                        onChange={(e, v) => setFilters({ ...filters, experienceCenterId: v })}
                        options={experienceCenters}
                        getOptionLabel={(option) => option.name || option.code || ""}
                        getOptionSelected={(option, value) => option._id === value._id}
                        className={`${classes.field} ${classes.ecField}`}
                        noOptionsText={
                            filters.city.length
                                ? "No experience centre in the selected city"
                                : "No experience centres found"
                        }
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Experience Center"
                                variant="outlined"
                                placeholder={filters.experienceCenterId.length ? "" : "All centres"}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {ecLoading ? (
                                                <CircularProgress size={14} thickness={5} style={{ color: ORANGE_DARK }} />
                                            ) : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />
                    <div className={classes.filterActions}>
                        {isDirty && !loading && (
                            <span className={classes.dirtyBadge}>
                                <span className={classes.dirtyDot} />
                                Not applied yet
                            </span>
                        )}
                        <Button
                            type="primary"
                            icon={<SearchIcon style={{ fontSize: 16, marginRight: 6 }} />}
                            onClick={() => fetchDashboard()}
                            className={`${classes.applyButton} ${isDirty && !loading ? classes.applyButtonDirty : ""}`}
                            loading={loading}
                            disabled={loading}
                        >
                            {loading ? "Loading..." : isDirty ? "Apply changes" : "Apply"}
                        </Button>
                        <Button
                            icon={<ClearIcon style={{ fontSize: 16, marginRight: 6 }} />}
                            onClick={clearFilters}
                            className={classes.clearButton}
                            disabled={loading}
                        >
                            Clear
                        </Button>
                    </div>
                </div>

                {/* Always rendered so the card does not change height when the
                    hint appears - the row above would otherwise jump. */}
                {/* Always rendered so the card does not change height when the
                    hint appears - the row above would otherwise jump. The
                    "press Apply" line outranks the centre count: it is the one
                    that explains why the numbers below have not moved. */}
                <div
                    className={classes.filterHint}
                    style={isDirty && !loading ? { color: "#9c4a16", fontWeight: 700 } : undefined}
                >
                    {ecLoading && (
                        <>
                            <CircularProgress size={11} thickness={5} style={{ color: ORANGE_DARK }} />
                            Loading the experience centres for that city…
                        </>
                    )}
                    {!ecLoading && isDirty && (
                        <>
                            <SearchIcon style={{ fontSize: 13 }} />
                            Filters changed — press <b style={{ margin: "0 2px" }}>Apply</b> to update the dashboard.
                        </>
                    )}
                    {!ecLoading && !isDirty && filters.city.length > 0 && (
                        <>
                            <PlaceOutlinedIcon style={{ fontSize: 13 }} />
                            {`${experienceCenters.length} experience centre${experienceCenters.length === 1 ? "" : "s"} in the selected ${filters.city.length === 1 ? "city" : "cities"}`}
                            {filters.experienceCenterId.length > 0
                                && ` · ${filters.experienceCenterId.length} selected`}
                        </>
                    )}
                </div>
            </Paper>

            {/* One page-level banner rather than one per panel. The charts and
                the table each already show their own thin bar; this is the
                single place that says WHAT is loading, which matters most on a
                refresh - the old figures stay on screen then, so without it
                there is nothing to explain why they have not changed yet. */}
            {loading && (
                <div className={classes.loadingBanner}>
                    <CircularProgress size={17} thickness={5} style={{ color: ORANGE_DARK }} />
                    <div style={{ minWidth: 0 }}>
                        <div className={classes.loadingBannerTitle}>Fetching AI reviews…</div>
                        <div className={classes.loadingBannerHint}>
                            {entries.length
                                ? "Reloading for the selected range — the figures below stay visible until the new data lands."
                                : "Pulling every AI-reviewed Runo call in the selected range, with its verdict and reasoning."}
                        </div>
                    </div>
                </div>
            )}

            <Grid container spacing={2} style={{ marginBottom: 14 }}>
                <Grid item xs={12} md={4}>
                    <Card className={classes.panelCard}>
                        <CardContent style={{ padding: 18 }}>
                            <div className={classes.panelTitle}>Verdict split</div>
                            <div className={classes.panelHint}>
                                {uniqueMode
                                    ? "One per lead, counted on its latest verdict"
                                    : "What the AI decided, across every review in range"}
                            </div>
                            {/* Charts keep their last data during a refetch rather
                                than blanking out - the bar plus the dimming below
                                mark it as stale without the layout collapsing. */}
                            {loading && (
                                <div style={{ margin: "0 0 10px 13px" }}>
                                    <CardLinearProgress />
                                </div>
                            )}
                            {loading && !hasVerdictData ? (
                                <div style={{ padding: "10px 4px 0" }}>
                                    <Skeleton variant="circle" width={210} height={210} style={{ margin: "0 auto" }} />
                                    <div className={classes.legendList}>
                                        {VERDICT_ORDER.map((v) => (
                                            <Skeleton key={v} variant="rect" height={38} style={{ borderRadius: 11 }} />
                                        ))}
                                    </div>
                                </div>
                            ) : hasVerdictData ? (
                                <div
                                    style={{
                                        opacity: loading ? 0.45 : 1,
                                        transition: "opacity 0.25s ease",
                                        pointerEvents: loading ? "none" : "auto",
                                    }}
                                >
                                    <div style={{ position: "relative", maxWidth: 270, height: 240, margin: "6px auto 0" }}>
                                        <Doughnut
                                            data={donutData}
                                            options={{
                                                maintainAspectRatio: false,
                                                cutout: "68%",
                                                plugins: {
                                                    legend: { display: false },
                                                    tooltip: {
                                                        padding: 10,
                                                        callbacks: {
                                                            label: (ctx) => {
                                                                const value = ctx.parsed || 0;
                                                                const pct = verdictTotal ? Math.round((value / verdictTotal) * 100) : 0;
                                                                return ` ${ctx.label}: ${value} (${pct}%)`;
                                                            },
                                                        },
                                                    },
                                                },
                                            }}
                                        />
                                        <div
                                            style={{
                                                position: "absolute",
                                                top: "50%",
                                                left: "50%",
                                                transform: "translate(-50%, -50%)",
                                                textAlign: "center",
                                                pointerEvents: "none",
                                            }}
                                        >
                                            <div style={{ fontSize: 34, fontWeight: 700, color: ORANGE_DARK, lineHeight: 1 }}>
                                                {verdictTotal}
                                            </div>
                                            <div style={{ fontSize: 11, color: "#9aa4b4", marginTop: 5, letterSpacing: "0.03em" }}>
                                                {uniqueMode ? "Leads" : "Reviewed"}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={classes.legendList}>
                                        {VERDICT_ORDER.map((v) => {
                                            const count = verdictCounts[v];
                                            const pct = verdictTotal ? Math.round((count / verdictTotal) * 100) : 0;
                                            return (
                                                <div
                                                    key={v}
                                                    className={classes.legendRow}
                                                    style={{
                                                        background: `linear-gradient(90deg, ${DONUT_COLORS[v]}1f ${pct}%, #FAFBFD ${pct}%)`,
                                                    }}
                                                >
                                                    <span className={classes.legendDot} style={{ backgroundColor: DONUT_COLORS[v] }} />
                                                    <span className={classes.legendLabel}>{VERDICT_META[v].label}</span>
                                                    <span className={classes.legendValue}>{count}</span>
                                                    <span className={classes.legendPct}>{pct}%</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                    {summary?.excludedCount ? (
                                        <div className={classes.excludedNote}>
                                            {`+${summary.excludedCount} classifier call${summary.excludedCount === 1 ? "" : "s"} with no verdict (excluded) — ${summary.totalLogsInRange} total in range`}
                                        </div>
                                    ) : null}
                                </div>
                            ) : (
                                <EmptyState classes={classes} message="No data in this range." />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={12} md={8}>
                    <Card className={classes.panelCard}>
                        <CardContent style={{ padding: 18 }}>
                            <div className={classes.panelTitle}>Why leads were sent for review</div>
                            <div className={classes.panelHint}>
                                {uniqueMode
                                    ? "Reason on each lead's latest Runo call, counted once per lead"
                                    : "Reason logged on the Runo call before the AI weighed in"}
                            </div>
                            {loading && (
                                <div style={{ margin: "0 0 10px 13px" }}>
                                    <CardLinearProgress />
                                </div>
                            )}
                            {loading && !reasonEntries.length ? (
                                <div style={{ height: 372, marginTop: 6, display: "flex", flexDirection: "column", justifyContent: "space-around" }}>
                                    {/* Descending widths so the placeholder reads as
                                        a sorted bar chart, which is what lands. */}
                                    {[92, 78, 64, 55, 43, 31, 22].map((w) => (
                                        <Skeleton key={w} variant="rect" height={26} width={`${w}%`} style={{ borderRadius: 5 }} />
                                    ))}
                                </div>
                            ) : reasonEntries.length ? (
                                <div
                                    style={{
                                        height: 372,
                                        marginTop: 6,
                                        opacity: loading ? 0.45 : 1,
                                        transition: "opacity 0.25s ease",
                                        pointerEvents: loading ? "none" : "auto",
                                    }}
                                >
                                    <Bar
                                        data={reasonChartData}
                                        options={{
                                            indexAxis: "y",
                                            maintainAspectRatio: false,
                                            plugins: {
                                                legend: { display: false },
                                                tooltip: {
                                                    padding: 10,
                                                    callbacks: {
                                                        label: (ctx) => {
                                                            const n = ctx.parsed.x;
                                                            const noun = uniqueMode ? "lead" : "review";
                                                            return ` ${n} ${noun}${n === 1 ? "" : "s"}`;
                                                        },
                                                    },
                                                },
                                            },
                                            scales: {
                                                x: {
                                                    ticks: { precision: 0, color: "#8b93a6", font: { size: 11 } },
                                                    grid: { color: "#eef1f6", drawBorder: false },
                                                },
                                                y: {
                                                    ticks: { color: "#5c6779", font: { size: 11.5 } },
                                                    grid: { display: false, drawBorder: false },
                                                },
                                            },
                                        }}
                                    />
                                </div>
                            ) : (
                                <EmptyState classes={classes} message="No data in this range." />
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card className={classes.tableCard}>
                <CardContent>
                    <div
                        className={classes.tableHeaderRow}
                        style={{ opacity: loading ? 0.55 : 1, pointerEvents: loading ? "none" : "auto" }}
                    >
                        <div className={classes.viewToggle}>
                            {[
                                {
                                    mode: false,
                                    label: "All reviews",
                                    count: allEntries.length,
                                    Icon: LayersOutlinedIcon,
                                    tip: "Every classifier review — a lead reviewed 3 times appears as 3 rows",
                                },
                                {
                                    mode: true,
                                    label: "Unique leads",
                                    count: uniqueEntriesData.length,
                                    Icon: FilterNoneOutlinedIcon,
                                    tip: "One row per lead, showing its latest verdict — expand a row to read every review it had",
                                },
                            ].map(({ mode, label, count, Icon, tip }) => {
                                const active = uniqueMode === mode;
                                const unavailable = mode && data && !supportsUnique;
                                return (
                                    <MuiTooltip
                                        key={label}
                                        title={unavailable ? "Unavailable — this server hasn't been updated yet" : tip}
                                    >
                                        <button
                                            type="button"
                                            disabled={loading || unavailable}
                                            onClick={() => setUniqueMode(mode)}
                                            className={`${classes.viewToggleBtn} ${active ? classes.viewToggleBtnActive : ""}`}
                                        >
                                            <Icon style={{ fontSize: 15 }} />
                                            {label}
                                            <span
                                                className={`${classes.viewToggleCount} ${active ? classes.viewToggleCountActive : ""}`}
                                            >
                                                {count}
                                            </span>
                                        </button>
                                    </MuiTooltip>
                                );
                            })}
                        </div>
                        <div className={classes.chipGroup}>
                            {["all", "real", "hold", "junk"].map((v) => {
                                const count = v === "all" ? entries.length : verdictCounts[v];
                                return (
                                    <Chip
                                        key={v}
                                        className={classes.filterChip}
                                        label={`${v === "all" ? "All" : VERDICT_META[v].label} · ${count}`}
                                        onClick={() => setVerdictFilter(v)}
                                        disabled={loading}
                                        style={
                                            verdictFilter === v
                                                ? {
                                                    background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE})`,
                                                    color: "#fff",
                                                    border: "1px solid transparent",
                                                    boxShadow: "0 3px 9px rgba(193,83,31,0.3)",
                                                }
                                                : { backgroundColor: "#fff", color: "#5c6779", border: "1px solid #e4e9f1" }
                                        }
                                    />
                                );
                            })}
                        </div>
                        <div style={{ display: "flex", gap: 10, marginLeft: "auto" }}>
                            <TextField
                                size="small"
                                variant="outlined"
                                placeholder="Search lead no, customer, reason…"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                className={classes.searchField}
                                style={{ minWidth: 280 }}
                                disabled={loading}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon style={{ fontSize: 18, color: "#9aa4b4" }} />
                                        </InputAdornment>
                                    ),
                                    endAdornment: search ? (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                className={classes.searchClearBtn}
                                                onClick={() => setSearch("")}
                                                disabled={loading}
                                            >
                                                <ClearIcon style={{ fontSize: 15 }} />
                                            </IconButton>
                                        </InputAdornment>
                                    ) : null,
                                }}
                            />
                            <Button
                                icon={<GetAppIcon style={{ fontSize: 16, marginRight: 6 }} />}
                                onClick={downloadExcel}
                                className={classes.downloadButton}
                                disabled={loading}
                            >
                                Download Excel
                            </Button>
                        </div>
                    </div>
                    {/* Only meaningful once duplicates are collapsed - in the
                        all-reviews view these figures would describe rows the
                        user can already see individually. */}
                    {uniqueMode && summary ? (
                        <div className={classes.uniqueInsightBar}>
                            <span className={classes.insightPill}>
                                <FilterNoneOutlinedIcon style={{ fontSize: 14, color: ORANGE_DARK }} />
                                <span className={classes.insightPillValue}>{summary.duplicateCount || 0}</span>
                                duplicate row{(summary.duplicateCount || 0) === 1 ? "" : "s"} collapsed
                            </span>
                            <span className={classes.insightPill}>
                                <AutorenewOutlinedIcon style={{ fontSize: 14, color: ORANGE_DARK }} />
                                <span className={classes.insightPillValue}>{summary.reReviewedCount || 0}</span>
                                lead{(summary.reReviewedCount || 0) === 1 ? "" : "s"} reviewed more than once
                            </span>
                            <MuiTooltip title="The AI reached a different verdict on a later review of the same lead — worth a look, since only the latest one counts here">
                                <span
                                    className={classes.insightPill}
                                    style={
                                        summary.verdictChangedCount
                                            ? { borderColor: "#f5c2c0", background: "#FFF6F5" }
                                            : undefined
                                    }
                                >
                                    <CompareArrowsOutlinedIcon
                                        style={{ fontSize: 14, color: summary.verdictChangedCount ? CRITICAL : ORANGE_DARK }}
                                    />
                                    <span
                                        className={classes.insightPillValue}
                                        style={summary.verdictChangedCount ? { color: CRITICAL } : undefined}
                                    >
                                        {summary.verdictChangedCount || 0}
                                    </span>
                                    changed verdict between reviews
                                </span>
                            </MuiTooltip>
                        </div>
                    ) : null}
                    <div className={classes.tableHint}>
                        <KeyboardArrowDownIcon style={{ fontSize: 15 }} />
                        {uniqueMode
                            ? "One row per lead, showing its latest verdict — expand a row to read every AI review it had, oldest to newest. "
                            : "Click a row or the arrow to view the AI's full analysis & sales tips — "}
                        click the <span style={{ color: BLUE, fontWeight: 700 }}>lead number</span> to open that lead's page
                        instead.
                    </div>
                    {/* Reserved slot rather than a conditional element, so the
                        table doesn't shift down by 4px each time a fetch starts. */}
                    <div className={classes.tableProgressSlot}>{loading ? <CardLinearProgress /> : null}</div>
                    <TableContainer style={{ overflow: "auto", maxHeight: 520, border: "1px solid #eaeff6", borderRadius: 12 }}>
                        <Table size="small" style={{ borderCollapse: "separate", borderSpacing: 0 }}>
                            <TableHead>
                                <TableRow>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2, width: 40 }} />
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Lead No.</StyledHeadCell>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Customer</StyledHeadCell>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Experience Center</StyledHeadCell>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Reason for Review</StyledHeadCell>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Verdict</StyledHeadCell>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Reviewed On</StyledHeadCell>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Lead Created</StyledHeadCell>
                                    <StyledHeadCell style={{ position: "sticky", top: 0, zIndex: 2 }}>Lead Assigned</StyledHeadCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <SkeletonRows classes={classes} rows={rowsPerPage > 10 ? 10 : rowsPerPage} />
                                ) : filteredEntries.length ? (
                                    paginatedEntries.map((entry) => {
                                        const v = verdictOf(entry);
                                        const meta = VERDICT_META[v];
                                        const isOpen = openRowId === entry.id;
                                        return (
                                            <React.Fragment key={entry.id}>
                                                <StyledBodyRow
                                                    hover
                                                    style={{ cursor: "pointer" }}
                                                    onClick={() => setOpenRowId(isOpen ? null : entry.id)}
                                                >
                                                    <TableCell style={cellBorder}>
                                                        <MuiTooltip
                                                            title={
                                                                isOpen
                                                                    ? "Hide AI analysis & sales tips"
                                                                    : entry.reviewCount > 1
                                                                        ? `View all ${entry.reviewCount} AI reviews & sales tips`
                                                                        : "View AI analysis & sales tips"
                                                            }
                                                        >
                                                            <IconButton size="small">
                                                                {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                                                            </IconButton>
                                                        </MuiTooltip>
                                                    </TableCell>
                                                    <TableCell style={{ ...cellBorder, fontWeight: 600 }}>
                                                        {entry.leadId ? (
                                                            <MuiTooltip title="Open this lead's page in a new tab">
                                                                <span
                                                                    className={classes.leadLink}
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        window.open(`/project-logs/${entry.leadId}`, "_blank", "noopener,noreferrer");
                                                                    }}
                                                                >
                                                                    #{entry.lead_no ?? "-"}
                                                                </span>
                                                            </MuiTooltip>
                                                        ) : (
                                                            `#${entry.lead_no ?? "-"}`
                                                        )}
                                                    </TableCell>
                                                    <TableCell style={cellBorder}>
                                                        <div style={{ fontWeight: 600, color: "#2b3648" }}>
                                                            {entry.customerName || "Unknown"}
                                                            {/* Both badges only ever appear in unique mode -
                                                                reviewCount/verdictChanged are set by the
                                                                grouping step and absent otherwise. */}
                                                            {entry.reviewCount > 1 ? (
                                                                <MuiTooltip
                                                                    title={`Reviewed ${entry.reviewCount} times — expand to read all ${entry.reviewCount} analyses`}
                                                                >
                                                                    <span className={classes.reviewCountBadge}>
                                                                        <HistoryOutlinedIcon style={{ fontSize: 11 }} />
                                                                        {`×${entry.reviewCount}`}
                                                                    </span>
                                                                </MuiTooltip>
                                                            ) : null}
                                                            {entry.verdictChanged ? (
                                                                <MuiTooltip
                                                                    title={`The AI changed its mind on this lead: ${[...entry.history]
                                                                        .reverse()
                                                                        .map((h) => VERDICT_META[verdictOf(h)].label)
                                                                        .join(" → ")}`}
                                                                >
                                                                    <span className={classes.changedBadge}>
                                                                        <ReportProblemOutlinedIcon style={{ fontSize: 11 }} />
                                                                        Changed
                                                                    </span>
                                                                </MuiTooltip>
                                                            ) : null}
                                                        </div>
                                                        <div style={{ fontSize: 11.5, color: "#8b93a6", marginTop: 2 }}>{entry.currentStage || ""}</div>
                                                    </TableCell>
                                                    {/* Centre over city, in one column. Two columns would
                                                        push this table to ten, and the city is never read
                                                        without the centre beside it anyway. Excel gets them
                                                        as separate columns, where sorting wants that. */}
                                                    <TableCell style={{ ...cellBorder, whiteSpace: "nowrap" }}>
                                                        <div style={{ fontWeight: 600, color: "#2b3648" }}>
                                                            {entry.experienceCenter || "—"}
                                                        </div>
                                                        <div style={{ fontSize: 11.5, color: "#8b93a6", marginTop: 2, display: "flex", alignItems: "center", gap: 3 }}>
                                                            {entry.city ? (
                                                                <>
                                                                    <PlaceOutlinedIcon style={{ fontSize: 11 }} />
                                                                    {entry.city}
                                                                </>
                                                            ) : (
                                                                "No city on record"
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                    <TableCell style={{ ...cellBorder, color: "#5c6779" }}>{entry.reasonForReview || "N/A"}</TableCell>
                                                    <TableCell style={cellBorder}>
                                                        <Chip
                                                            size="small"
                                                            label={meta.label}
                                                            style={{
                                                                backgroundColor: `${meta.color}16`,
                                                                color: meta.color,
                                                                fontWeight: 700,
                                                                border: `1px solid ${meta.color}33`,
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell style={{ ...cellBorder, color: "#5c6779", whiteSpace: "nowrap" }}>
                                                        {moment(entry.createdAt).format("DD MMM, hh:mm A")}
                                                    </TableCell>
                                                    <TableCell style={{ ...cellBorder, color: "#5c6779", whiteSpace: "nowrap" }}>
                                                        {entry.leadCreatedAt ? moment(entry.leadCreatedAt).format("DD MMM YYYY") : "-"}
                                                    </TableCell>
                                                    <TableCell style={{ ...cellBorder, color: "#5c6779", whiteSpace: "nowrap" }}>
                                                        {entry.leadAssignedDate ? moment(entry.leadAssignedDate).format("DD MMM YYYY") : "-"}
                                                    </TableCell>
                                                </StyledBodyRow>
                                                {isOpen ? <DetailRow entry={entry} classes={classes} /> : null}
                                            </React.Fragment>
                                        );
                                    })
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={9} style={{ ...cellBorder, padding: 0 }}>
                                            <EmptyState classes={classes} message="No reviews match this filter." />
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <TablePagination
                        component="div"
                        count={filteredEntries.length}
                        page={page}
                        onPageChange={(e, newPage) => setPage(newPage)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => {
                            setRowsPerPage(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                    />
                </CardContent>
            </Card>
        </div>
    );
};

export default connect(null, { setSnackInfo })(MoveToMarketingClassifierDashboard);
